import { useEffect, useRef, useState } from 'react'
import classnames from 'classnames/bind'
import { format } from 'date-fns'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useQuery } from 'react-query'

// Libs
import { fetchAuctionLots } from '@/api/fetchAuctionLots'
import { useLiveList } from '@/components/shared/LiveList/hooks/useLiveList'
import { useLiveItemRoomData } from '@/hooks/useLiveItemRoomData'
import { getLiveRoomPath, goToLiveRoom } from '@/methods/goToLiveRoom'
import { LIVE_TRACK_EVENT, liveSourceFromPathname, trackLiveEvent } from '@/methods/liveTracking'
import useLiveImpression from '@/hooks/useLiveImpression'
import handleImageBlur from '@/methods/handleImageBlur'
import useI18n from '@/hooks/useI18n'
import dynamic from 'next/dynamic'

// Components
import Image, { PlaceholderType } from '@/components/common/Image'
import Icon from '@/assets/icons'
import LottieIcon from './LottieIcon'

// Lottie（UX micro interaction / Bid、Auction）
import bidAnimation from '@/assets/lotties/auction-bid.json'
import hammerAnimation from '@/assets/lotties/auction-hammer.json'

// Constants
import { envConfigs } from '@/constants/config'
import { LIVE_SOURCE_TYPE, ROOM_LIVE_TYPE, ROOM_MODE } from '@/constants/liveRoom'
import { QUERY_KEY } from '@/constants/queryKey'
import { AUCTION_LOT_STATUS } from '@/components/pages/AuctionLiveRoom/constants'

// Methods
import { getCompanyBrandName } from '@/components/pages/Terms/methods/company'

// Types
import type { LiveListType } from '@/components/shared/LiveList/hooks/useLiveList'
import type { IAuctionLot, IAuctionLotsData } from '@/types/auction'

// Styles
import styles from './style.module.scss'

const LiveItemPlayer = dynamic(() => import('@/components/shared/LiveItemPlayer'), { ssr: false })

const cx = classnames.bind(styles)

/** 設計稿的輪播點最多 5 個 */
const MAX_ROOMS = 5

/** 金額是「分」整數，顯示時除以 100 */
const formatAmount = (cents?: number) => (Number(cents || 0) / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })

/** 設計稿原價寫 $1,000：台幣用 $，其他幣別前面帶幣別代碼 */
const formatPrice = (cents?: number, currency?: string) => {
  const prefix = !currency || currency === 'TWD' ? '$' : `${currency} `
  return `${prefix}${formatAmount(cents)}`
}

/** google calendar 要 20210831T100000 這種格式（同 LiveItem） */
const formatCalendarTime = (time: string) => time.replace(/-/g, '').replace(/:/g, '').replace('+0800', '')

/**
 * 首頁「限時競標」區塊（Figma 首頁V2 1269:119018 / 1423:144901 / 直播競標_3）。
 *
 *   - ≥1200：三欄，左邊直播資訊、中間直式直播畫面、右邊競標商品清單
 *   - 710–1199：兩欄，直播資訊疊在直播畫面下緣
 *   - <710：直式卡片，商品清單在下方橫向滑動
 *
 * 直播中與預告的競標房都會出現（直播中排前面），多間時可以切換；
 * 商品清單用 GET /v2/room/{roomId}/auction/lots：起標價模糊顯示（直播驚爆價），下面是原價。
 */
const AuctionLive = () => {
  const { t } = useTranslation()
  const router = useRouter()
  const { isOverseaUser } = useI18n()

  const { data: liveRoomListRaw } = useLiveList({
    params: {
      pageSize: MAX_ROOMS,
      liveTypes: [ROOM_LIVE_TYPE.CURRENT, ROOM_LIVE_TYPE.FUTURE],
      roomMode: ROOM_MODE.AUCTION,
    },
  })

  // 後端已依 room_mode 篩選；前端再濾一次，避免後端還沒上版時把一般直播混進來
  const auctionRooms = (liveRoomListRaw?.pages.flatMap((page) => page?.roomList ?? []) ?? [])
    .filter((room): room is LiveListType => !!room && room.roomMode === ROOM_MODE.AUCTION)
    .sort((a, b) => Number(b.liveType === ROOM_LIVE_TYPE.CURRENT) - Number(a.liveType === ROOM_LIVE_TYPE.CURRENT))
    .slice(0, MAX_ROOMS)

  const [activeIndex, setActiveIndex] = useState(0)
  const room: LiveListType | undefined = auctionRooms[Math.min(activeIndex, auctionRooms.length - 1)]
  const roomId = room?.roomId ?? ''

  const isLive = room?.liveType === ROOM_LIVE_TYPE.CURRENT
  const isFuture = room?.liveType === ROOM_LIVE_TYPE.FUTURE

  const { data: lotsData } = useQuery<IAuctionLotsData>([QUERY_KEY.FETCH_AUCTION_LOTS, roomId], () => fetchAuctionLots({ roomId }), {
    enabled: !!roomId,
  })
  // 無庫存可標的排最後，其餘照 sort（同直播間的清單順序，首頁不處理釘選）
  const lots: IAuctionLot[] = [...(lotsData?.lots ?? [])].sort(
    (a, b) => Number(a.status === AUCTION_LOT_STATUS.UNAVAILABLE) - Number(b.status === AUCTION_LOT_STATUS.UNAVAILABLE) || a.sort - b.sort,
  )

  // 直播中才拉串流；預告房只顯示封面
  const { roomLivingData, hasLiveUrl } = useLiveItemRoomData({ roomId, isLive })
  const videoRef = useRef(null)
  const [isShowPlayer, setIsShowPlayer] = useState(false)
  const isMP4 = room?.liveSourceType === LIVE_SOURCE_TYPE.MP4

  // 日期在 client 端才格式化，避免 SSR 時區不同造成 hydration 落差（同 LiveItem）
  const [startTimeText, setStartTimeText] = useState('')

  useEffect(() => {
    setIsShowPlayer(false)
    setStartTimeText(room?.startTime ? format(new Date(room.startTime), 'MM-dd HH:mm') : '')
  }, [roomId, room?.startTime])

  const screenRef = useRef<HTMLDivElement>(null)
  useLiveImpression({ ref: screenRef, type: LIVE_TRACK_EVENT.COVER_IMPRESSION, roomId, source: liveSourceFromPathname(router.pathname) })

  if (!room) return null

  const roomCount = auctionRooms.length
  const handlePrev = () => setActiveIndex((index) => (index - 1 + roomCount) % roomCount)
  const handleNext = () => setActiveIndex((index) => (index + 1) % roomCount)

  const handleEnterRoom = () =>
    goToLiveRoom({ router, roomId, liveType: room.liveType, liveSourceType: room.liveSourceType, roomMode: room.roomMode })

  const handleReserve = () => {
    trackLiveEvent({ type: LIVE_TRACK_EVENT.RESERVE, roomId, source: liveSourceFromPathname(router.pathname) })
    const liveUrl = `${envConfigs.domainUrl}${getLiveRoomPath({ roomId, roomMode: room.roomMode })}`
    const title = t('Live_Calandar_Title', { kolName: room.kolName, title: room.title, brandName: getCompanyBrandName(isOverseaUser) })
    const description = t('Live_Calandar_Description', { displayDateWithTime: startTimeText, liveUrl })

    window.open(
      `https://calendar.google.com/calendar/render?action=TEMPLATE&dates=${formatCalendarTime(room.startTime)}/${formatCalendarTime(
        room.endTime,
      )}&details=${description}&text=${title}`,
    )
  }

  const renderStatus = (className?: string) => (
    <div className={cx('status', className)}>
      {isLive && (
        <>
          <span className={cx('status-tag', 'status-tag--live')}>
            {t('Home_Auction_Live_Bidding')}
            <Image src='/images/svg/home-auction/hot.svg' width={12} height={15} alt='' />
          </span>
          <span className={cx('status-text')}>{t('Common_Views', { views: room.dummyViews })}</span>
        </>
      )}
      {isFuture && (
        <>
          <span className={cx('status-tag', 'status-tag--future')}>{t('Live_Future')}</span>
          <span className={cx('status-text')}>{startTimeText}</span>
        </>
      )}
    </div>
  )

  const renderKol = (className?: string) => (
    <div className={cx('kol', className)}>
      <span className={cx('kol-avatar')}>
        <Image src={room.kolHeadshotUrl} width={36} height={36} alt={room.kolName} style={{ objectFit: 'cover' }} />
      </span>
      <span className={cx('kol-name')}>{room.kolName}</span>
    </div>
  )

  // mobile 的切換鈕疊在畫面上，畫面本身點了會進直播間，所以要擋住冒泡
  const renderNav = (direction: 'vertical' | 'horizontal') =>
    roomCount > 1 && (
      <div className={cx('nav', `nav--${direction}`)} onClick={(event) => event.stopPropagation()}>
        <button type='button' className={cx('nav-arrow', 'nav-arrow--prev')} onClick={handlePrev} aria-label='previous'>
          <Icon.ArrowLeft />
        </button>
        {direction === 'vertical' && (
          <div className={cx('nav-dots')}>
            {auctionRooms.map((item, index) => (
              <button
                type='button'
                key={item.roomId}
                className={cx('nav-dot', { isActive: index === activeIndex })}
                onClick={() => setActiveIndex(index)}
                aria-label={item.title}
              />
            ))}
          </div>
        )}
        <button type='button' className={cx('nav-arrow', 'nav-arrow--next')} onClick={handleNext} aria-label='next'>
          <Icon.ArrowLeft />
        </button>
      </div>
    )

  return (
    <section className={cx('auction-live')}>
      <div className={cx('card')}>
        <div className={cx('header')}>
          <span className={cx('header-bg')}>
            <Image src='/images/svg/home-auction/title-bg.svg' fill alt='' />
          </span>
          <span className={cx('header-bg', 'header-bg--mobile')}>
            <Image src='/images/svg/home-auction/title-bg-mobile.svg' fill alt='' />
          </span>
          <LottieIcon className={cx('header-icon')} animationData={bidAnimation} />
          <h2 className={cx('header-title')}>{t('Home_Auction_Live_Title')}</h2>
          <LottieIcon className={cx('header-icon')} animationData={hammerAnimation} />
        </div>

        <div className={cx('stage')}>
          <div className={cx('stage-background')}>
            <Image src={room.coverPhotoUrl} fill alt='' style={{ objectFit: 'cover' }} />
          </div>

          <div className={cx('info')}>
            {renderStatus()}
            <h3 className={cx('info-title')}>{room.title}</h3>
            {renderKol()}
          </div>

          <div className={cx('screen')} onClick={handleEnterRoom} ref={screenRef}>
            {/* 設計稿：畫面本身 contain，留白處用同一張圖模糊填滿 */}
            <div className={cx('screen-blur')}>
              <Image src={room.coverPhotoUrl} fill alt='' style={{ objectFit: 'cover' }} sizes='335px' />
            </div>
            <Image
              src={room.coverPhotoUrl}
              fill
              alt={room.title}
              style={{ objectFit: 'contain' }}
              placeholder={PlaceholderType.blur}
              blurDataURL={handleImageBlur()}
              sizes='(max-width: 709px) 100vw, 335px'
            />

            {isLive && (hasLiveUrl || isMP4) && (
              <LiveItemPlayer
                key={roomId}
                className={cx('screen-player', { isShow: isShowPlayer })}
                wrapperClassName={cx('screen-player-wrapper')}
                videoRef={videoRef}
                src={isMP4 ? (room.liveSource as string) : roomLivingData?.liveUrlInfo[0]?.streamUrl}
                sessionRoomId={roomLivingData?.gdata?.sessionRoomId}
                isShowLiveItemPlayer={isShowPlayer}
                setIsShowLiveItemPlayer={setIsShowPlayer}
                liveTime={room.liveTime}
              />
            )}

            <div className={cx('screen-mask')} />

            <div className={cx('screen-info')}>
              <div className={cx('screen-info-row')}>
                {renderKol('kol--small')}
                {renderStatus()}
              </div>
              <p className={cx('screen-info-title')}>{room.title}</p>
            </div>

            {isLive ? (
              <button
                type='button'
                className={cx('screen-button', 'screen-button--join')}
                onClick={(event) => {
                  event.stopPropagation()
                  handleEnterRoom()
                }}
              >
                {t('Home_Auction_Live_Join')}
                <LottieIcon className={cx('screen-button-lottie')} animationData={bidAnimation} />
              </button>
            ) : (
              <button
                type='button'
                className={cx('screen-button', 'screen-button--reserve')}
                onClick={(event) => {
                  event.stopPropagation()
                  handleReserve()
                }}
              >
                {t('Home_Auction_Live_Reserve')}
                <span className={cx('screen-button-icon')}>
                  <Icon.Calendar />
                </span>
              </button>
            )}

            <div className={cx('screen-nav')}>{renderNav('horizontal')}</div>
          </div>

          <div className={cx('stage-nav')}>{renderNav('vertical')}</div>
        </div>

        <aside className={cx('lots')}>
          <div className={cx('lots-header')}>
            <p className={cx('lots-header-title')}>
              {t('Auction_ProductList_Title')} {lots.length}
            </p>
            <p className={cx('lots-header-note')}>{t('Auction_ProductList_Note')}</p>
          </div>

          <div className={cx('lots-body')}>
            {lots.map((lot) => {
              const isSoldOut = lot.status === AUCTION_LOT_STATUS.UNAVAILABLE

              return (
                <div key={lot.lotId} className={cx('lot', { isSoldOut })} onClick={handleEnterRoom}>
                  <div className={cx('lot-image')}>
                    <Image src={lot.display?.imageUrl} fill alt={lot.display?.productName ?? ''} style={{ objectFit: 'cover' }} sizes='124px' />
                  </div>
                  <div className={cx('lot-content')}>
                    <p className={cx('lot-name')}>{lot.display?.productName}</p>
                    <div className={cx('lot-price')}>
                      {/* 設計稿：起標價模糊處理，進直播間才看得到 */}
                      <p className={cx('lot-price-live')}>
                        <span className={cx('lot-price-live-label')}>{t('Home_Auction_Live_Price')}</span>
                        <span className={cx('lot-price-live-value')} aria-hidden>
                          {formatAmount(lot.params?.startingPrice)}
                        </span>
                      </p>
                      {/* 後端 GET lots 還沒帶 orig_price 時不渲染，免得顯示 $0 */}
                      {!!lot.origPrice && (
                        <p className={cx('lot-price-orig')}>
                          <span className={cx('lot-price-orig-label')}>{t('Common_Original_Price')}</span>
                          <span className={cx('lot-price-orig-value')}>{formatPrice(lot.origPrice, lot.currency)}</span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </aside>
      </div>
    </section>
  )
}

export default AuctionLive
